// Eszköz-űrlap tiszta segédei (nem-komponens): form-értékek típusa, alapértékek, a @atlas/contracts
// write-sémája (a resolverhez) és a lokalizált zod-hibatérkép. Külön fájl a komponenstől, hogy a
// react-refresh (Fast Refresh) tiszta maradjon (az asset-form-sheet.tsx CSAK a komponenst exportálja).
// A minta a clients/accounts form.ts — a validálás forrás-igazsága a contract (NEM saját séma).
// Az IGÉNYLÉS-űrlap (asset-request-sheet) segédei is itt élnek — ugyanez a minta, a
// AssetRequestCreateBodySchema-ra terelve (a kategória CSAK UI-szűrő a típus-listához, nem wire-mező).
// A típus-/kategória-opciók a KATALÓGUSBÓL jönnek (`catalog.ts`) — itt nincs beégetett lista. A „kinek"
// (iroda VAGY személy) szabályát a contract `superRefine`-ja adja; a form csak a hiba-okokat fordítja.
import {
  AssetRequestCreateBodySchema,
  AssetRequestUpdateBodySchema,
  AssetWriteBodySchema,
  OFFICE_USAGE_LOCATION,
} from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { z } from 'zod';

/** Sentinel a „nincs" select-opcióhoz (a Radix Select nem enged üres value-t). */
export const NONE = '__none__';

/** A KÉZZEL választható státuszok (a contract enumja 1:1) — a workflow-státuszok a 2. increment. */
export const MANUAL_STATUS_OPTIONS = ['purchased', 'in_use', 'spare', 'retired'] as const;
export type ManualStatus = (typeof MANUAL_STATUS_OPTIONS)[number];

/** Igaz, ha a státusz a kézi halmazból való (a workflow-sorok szerkesztése tiltott). */
export function isManualStatus(status: string): status is ManualStatus {
  return (MANUAL_STATUS_OPTIONS as readonly string[]).includes(status);
}

/** A felhasználás-hely opciói a LISTA-szűrőn (a legacy UsageLocationEnum 1:1; a `social` az irodai eszközöké). */
export const USAGE_OPTIONS = ['office', 'home', 'social', 'mixed'] as const;

/** A SZEMÉLYHEZ rendelt eszköz választható helyei (az irodai eszköz helye fixen `social`) — a contract tükre. */
export const PERSONAL_USAGE_OPTIONS = ['office', 'home', 'mixed'] as const;

/** Az irodai (általános használatú) eszköz fix helye. */
export const OFFICE_USAGE = OFFICE_USAGE_LOCATION;

/** A form mező-értékei (string-alapú; a wire-alakra a `toAssetBody` normalizál: '' → null, NONE → null).
 *  A `status` üres ('') is lehet: nem-kézi státuszú (pl. `received`) sor szerkesztésekor a mező
 *  választásra kényszerít — a néma státusz-átírás (received→purchased) helyett. */
export interface AssetFormValues {
  name: string;
  type: string;
  status: ManualStatus | '';
  serialNumber: string;
  licenseKey: string;
  netPrice: string;
  vatRate: string;
  purchaseDate: string;
  retirementDate: string;
  /** Lejárat / megújítás napja — CSAK szoftver-típusnál látszik és megy a body-ba. */
  expiryDate: string;
  purchaseLocation: string;
  invoiceNumber: string;
  /** Irodai (általános) használat — ilyenkor nincs birtokos és a hely fixen `social`. */
  officeUse: boolean;
  holderIdentityId: string;
  usageLocation: string;
}

/** Üres (create) alapértékek. A típus üres: a katalógus betöltése után az első típusra áll (a form-sheet). */
export const EMPTY_ASSET_FORM: AssetFormValues = {
  name: '',
  type: '',
  status: 'purchased',
  serialNumber: '',
  licenseKey: '',
  netPrice: '',
  // A 27%-os áfa az alapértelmezett (a legacy és az igénylés-form is ezt tölti elő).
  vatRate: '27',
  purchaseDate: '',
  retirementDate: '',
  expiryDate: '',
  purchaseLocation: '',
  invoiceNumber: '',
  officeUse: false,
  holderIdentityId: '',
  usageLocation: NONE,
};

/** ISO (dátum vagy datetime) → 'YYYY-MM-DD' a date-inputhoz; üres/null → ''. */
export function toDateInput(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/** A contract write-body típusa (a nullableStr mezők preprocess-elt — '' → null a szerver-oldalon is). */
type AssetWriteBody = z.infer<typeof AssetWriteBodySchema>;

/**
 * Form-értékek → wire-body: '' → null a szabad-szöveges/dátum mezőkön, NONE-sentinel → null a selecteken.
 * A `retirementDate` CSAK retired státusznál megy — nem-retired-nél mindig null (a rejtett mező ürítve).
 */
export function toAssetBody(v: AssetFormValues, isSoftware: boolean): AssetWriteBody {
  const emptyToNull = (s: string): string | null => (s === '' ? null : s);
  const noneToNull = (s: string): string | null => (s === '' || s === NONE ? null : s);
  return {
    name: v.name,
    type: v.type,
    // Az üres status szándékosan átfolyik a zod-enumig → mező-szintű hiba (választás-kényszer).
    status: v.status as AssetWriteBody['status'],
    serialNumber: emptyToNull(v.serialNumber),
    licenseKey: emptyToNull(v.licenseKey),
    // A netPrice/vatRate KÖTELEZŐ (az Odin-oszlop NOT NULL) — trimmelve, nyersen megy: az üres érték a
    // contract regexén bukik, és a mező-szintű „kötelező" üzenetet kapja (nem néma szerver-422).
    netPrice: v.netPrice.trim(),
    vatRate: v.vatRate.trim(),
    purchaseDate: emptyToNull(v.purchaseDate),
    retirementDate: v.status === 'retired' ? emptyToNull(v.retirementDate) : null,
    // A lejárat CSAK szoftveren értelmezett: hardver-típusnál a (rejtett) mező értéke nem megy ki — a BFF is
    // `null`-ra állítja, itt a form a saját állapotában sem hagy félrevezető értéket a kérésben.
    expiryDate: isSoftware ? emptyToNull(v.expiryDate) : null,
    purchaseLocation: emptyToNull(v.purchaseLocation),
    invoiceNumber: emptyToNull(v.invoiceNumber),
    // iroda: nincs birtokos, a hely fix; személy: a birtokos kötelező (a contract ellenőrzi), a hely választható
    holderIdentityId: v.officeUse ? null : noneToNull(v.holderIdentityId),
    officeUse: v.officeUse,
    usageLocation: v.officeUse ? OFFICE_USAGE : noneToNull(v.usageLocation),
  } satisfies AssetWriteBody;
}

/**
 * A contract write-sémája + az üzleti szabály: status=retired → retirementDate KÖTELEZŐ (a szerver is
 * 422-t ad — itt az azonnali form-visszajelzés). Az issue üzenet nélkül megy → a lokalizált errorMap adja.
 */
export const assetFormSchema = AssetWriteBodySchema.superRefine((v, ctx) => {
  // A nullableStr mezők a contractban ZodTypeAny-k → a parse-olt objektum e mezői lazán tipizáltak;
  // a status/retirementDate viszont szigorú (enum + regex-es string) — a szabály ezekre épül.
  if (v.status === 'retired' && !v.retirementDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['retirementDate'] });
  }
});

/**
 * A netPrice/vatRate mező-hibájának lokalizált üzenete (a leltár- és az igénylés-form KÖZÖS ága): a regex-hiba
 * üres bemenetnél „kötelező", egyébként „formátum". A `raw` a mező nyers bemenete (`ctx.data`).
 */
function priceFieldMessage(t: TFunction, field: 'netPrice' | 'vatRate', raw: unknown): string {
  const empty = typeof raw === 'string' ? raw.trim() === '' : true;
  return empty ? t(`validation:assets.${field}.required`) : t(`validation:assets.${field}.format`);
}

/**
 * Az iroda/személy szabály (`refineOfficeRule` + `holder_required`) hiba-okának lokalizált üzenete — a leltár- és
 * az igénylés-form KÖZÖS ága. A contract az okot a `params.reason`-ben adja; ismeretlen ok → `null`.
 */
function officeRuleMessage(t: TFunction, issue: z.ZodIssueOptionalMessage): string | null {
  if (issue.code !== z.ZodIssueCode.custom) return null;
  const reason: unknown = issue.params?.reason;
  switch (reason) {
    case 'holder_required':
      return t('validation:assets.holder.required');
    case 'office_no_person':
      return t('validation:assets.office.noPerson');
    case 'office_social_only':
      return t('validation:assets.usageLocation.officeSocialOnly');
    case 'personal_location':
      return t('validation:assets.usageLocation.personal');
    default:
      return null;
  }
}

/**
 * Lokalizált zod-hibatérkép: a zod-issue (mező-path + kód) → a `validation:assets.*` réteg üzenete.
 * Ismeretlen issue az asset-mezőkön → lokalizált generikus üzenet; egyéb path → a zod alapértelmezettje.
 */
export function buildAssetErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    const officeMessage = officeRuleMessage(t, issue);
    if (officeMessage !== null) return { message: officeMessage };
    if (field === 'name') {
      if (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type) {
        return { message: t('validation:assets.name.required') };
      }
      if (issue.code === z.ZodIssueCode.too_big)
        return { message: t('validation:assets.name.max') };
    }
    if (
      field === 'type' &&
      (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type)
    ) {
      return { message: t('validation:assets.type.required') };
    }
    if (field === 'retirementDate' && issue.code === z.ZodIssueCode.custom) {
      return { message: t('validation:assets.retirementDate.required') };
    }
    if (field === 'netPrice' || field === 'vatRate') {
      return { message: priceFieldMessage(t, field, ctx.data) };
    }
    if (
      (field === 'purchaseDate' || field === 'retirementDate' || field === 'expiryDate') &&
      issue.code === z.ZodIssueCode.invalid_string
    ) {
      return { message: t('validation:assets.date.invalid') };
    }
    if (typeof field === 'string' && field in EMPTY_ASSET_FORM) {
      return { message: t('validation:assets.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract-sémából: a validálás a WIRE-alakon fut (a `toAssetBody` normalizálása után —
 * így az üres date-input ('') nem akad fenn a YYYY-MM-DD regexen), a hiba-pathok viszont 1:1 a form-mezők
 * nevei → közvetlenül a mezőre terelődnek. Sikerkor az EREDETI (string-alapú) form-értékek mennek tovább
 * (a wire-body-t a mutáció építi a `toAssetBody`-val).
 */
export function buildAssetResolver(
  t: TFunction,
  isSoftwareType: (type: string) => boolean,
): Resolver<AssetFormValues> {
  // A cast a wire-alak (nullable) és a form-értékek (string-alapú) generikus eltérését hidalja át — a
  // mező-NEVEK azonosak, a futásidejű viselkedés a zodResolver standardja (a clients/accounts minta).
  const base = zodResolver(assetFormSchema, {
    errorMap: buildAssetErrorMap(t),
  }) as unknown as Resolver<AssetFormValues>;
  return async (values, context, options) => {
    // A wire-alak (nullokkal) form-értékként megy a base-nek — csak a validáláshoz (lásd a fenti castot).
    const result = await base(
      toAssetBody(values, isSoftwareType(values.type)) as unknown as AssetFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}

// ---------------------------------------------------------------------------
// IGÉNYLÉS-űrlap (workflow B lépcső) — a create- és az edit-form közös segédei. A kategória CSAK a típus-lista
// UI-szűrője: a wire-body-ba NEM kerül (a BFF a típusból, a katalógusból származtatja).
// ---------------------------------------------------------------------------

/** Az igénylés-form mező-értékei (string-alapú; a wire-alakra a `toAssetRequestBody` normalizál). */
export interface AssetRequestFormValues {
  /** CSAK UI-szűrő a típus-listához (katalógus-kategória kulcsa) — a body-ba nem megy. */
  category: string;
  type: string;
  name: string;
  netPrice: string;
  vatRate: string;
  website: string;
  /** Az igénylő megjegyzése (indoklás, konfiguráció, határidő) — opcionális. */
  note: string;
  /** Irodának (általános használatra) igényelve — nincs „kinek", a hely fixen `social`. */
  officeUse: boolean;
  /** KINEK igényli (identity-id) — üres = magának (irodánál nincs). */
  requestedForIdentityId: string;
  /** Felhasználás helye (#122): NONE | office | home | mixed | social (irodánál fix). */
  usageLocation: string;
}

/** Üres (create) alapértékek az igénylés-formhoz; a kategória/típus a katalógus betöltése után áll be. */
export const EMPTY_ASSET_REQUEST_FORM: AssetRequestFormValues = {
  category: '',
  type: '',
  name: '',
  netPrice: '',
  vatRate: '27',
  website: '',
  note: '',
  officeUse: false,
  requestedForIdentityId: '',
  usageLocation: NONE,
};

/** A contract igénylés-body típusa (a felvétel és a szerkesztés azonos alakú). */
type AssetRequestBody = z.infer<typeof AssetRequestCreateBodySchema>;

/**
 * Igénylés form-értékek → wire-body: a `category` KIMARAD (UI-szűrő), üres website/megjegyzés → null. Irodánál
 * nincs „kinek" és a hely `social`. A netPrice/vatRate trimmelve megy (a regex-validálás a nyers stringen fut).
 */
export function toAssetRequestBody(v: AssetRequestFormValues): AssetRequestBody {
  return {
    type: v.type,
    name: v.name,
    netPrice: v.netPrice.trim(),
    vatRate: v.vatRate.trim(),
    website: v.website.trim() === '' ? null : v.website.trim(),
    note: v.note.trim() === '' ? null : v.note.trim(),
    officeUse: v.officeUse,
    requestedForIdentityId:
      v.officeUse || v.requestedForIdentityId === '' ? null : v.requestedForIdentityId,
    usageLocation: v.officeUse
      ? OFFICE_USAGE
      : v.usageLocation === '' || v.usageLocation === NONE
        ? null
        : v.usageLocation,
  } satisfies AssetRequestBody;
}

/**
 * Lokalizált zod-hibatérkép az igénylés-formhoz. A netPrice/vatRate regex-hibájánál az üres értéket
 * (required) és a rossz formátumot (format) a `ctx.data` (a mező nyers bemenete) különbözteti meg.
 */
export function buildAssetRequestErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    const officeMessage = officeRuleMessage(t, issue);
    if (officeMessage !== null) return { message: officeMessage };
    if (field === 'name') {
      if (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type) {
        return { message: t('validation:assets.name.required') };
      }
      if (issue.code === z.ZodIssueCode.too_big)
        return { message: t('validation:assets.name.max') };
    }
    if (
      field === 'type' &&
      (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type)
    ) {
      return { message: t('validation:assets.type.required') };
    }
    if (field === 'netPrice' || field === 'vatRate') {
      return { message: priceFieldMessage(t, field, ctx.data) };
    }
    if (field === 'website') {
      return { message: t('validation:assets.website.url') };
    }
    if (field === 'note' && issue.code === z.ZodIssueCode.too_big) {
      return { message: t('validation:assets.note.max') };
    }
    if (typeof field === 'string' && field in EMPTY_ASSET_REQUEST_FORM) {
      return { message: t('validation:assets.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver az igénylés-formhoz — a validálás a WIRE-alakon fut (contract AssetRequestCreateBodySchema /
 * AssetRequestUpdateBodySchema — a két séma azonos szabályú, a forrás-igazság), a hiba-pathok 1:1 a form-mezők
 * nevei. Sikerkor az EREDETI form-értékek mennek tovább (a wire-body-t a mutáció építi a `toAssetRequestBody`-val).
 */
export function buildAssetRequestResolver(
  t: TFunction,
  mode: 'create' | 'update' = 'create',
): Resolver<AssetRequestFormValues> {
  const base = zodResolver(
    mode === 'create' ? AssetRequestCreateBodySchema : AssetRequestUpdateBodySchema,
    { errorMap: buildAssetRequestErrorMap(t) },
  ) as unknown as Resolver<AssetRequestFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toAssetRequestBody(values) as unknown as AssetRequestFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}
